import React from "react";
import { Descriptions, Typography } from "antd";

import { Value } from "@components/Text";
import {
  type LinkTemplateKey,
  getDefaultLinkTemplatePath,
} from "@components/Organization/helpers/defaultLinkTemplatePaths";

const { Item } = Descriptions;
const { Text } = Typography;

const PLACEHOLDER_PATTERN = /(\{[^}]+\})/g;
const IS_PLACEHOLDER = /^\{[^}]+\}$/;

const LABEL_PORTAL_URL = "Portal";
const LABEL_SIGN_IN_URL = "Sign In";
const LABEL_INVESTMENT_URL = "Investment";
const LABEL_DOCUMENTS_URL = "Documents";
const LABEL_INVESTMENT_SIGN_URL = "PPM Signed";
const LABEL_INVESTMENT_SIGN_THANK_YOU_URL = "PPM Signed - Thank You";
const LABEL_SOFT_COMMITMENT_URL = "Soft Commitment";

const renderLabel = (text: string): JSX.Element => (
  <span style={{ whiteSpace: "nowrap" }}>{text}</span>
);

const renderLinkTemplateValue = (url?: string): JSX.Element => {
  const hasNoUrl = url === null || url === undefined || url === "";

  if (hasNoUrl) {
    return <Value value={url} />;
  }

  const parts = url.split(PLACEHOLDER_PATTERN);
  const hasPlaceholders = parts.some((part) => IS_PLACEHOLDER.test(part));

  if (!hasPlaceholders) {
    return <Value value={url} />;
  }

  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {parts.map((part, index) => {
        const isPlaceholder = IS_PLACEHOLDER.test(part);

        if (isPlaceholder) {
          return (
            <Text key={index} type="secondary">
              {part}
            </Text>
          );
        }

        return part;
      })}
    </span>
  );
};

type LinkTemplates = Partial<Record<LinkTemplateKey, string>>;

type Organization = {
  domain?: string;
  linkTemplates?: LinkTemplates;
};

const LINK_TEMPLATE_FIELDS: { key: LinkTemplateKey; label: string }[] = [
  { key: "portalUrl", label: LABEL_PORTAL_URL },
  { key: "softCommitmentUrl", label: LABEL_SOFT_COMMITMENT_URL },
  { key: "signInUrl", label: LABEL_SIGN_IN_URL },
  { key: "investmentUrl", label: LABEL_INVESTMENT_URL },
  { key: "documentsUrl", label: LABEL_DOCUMENTS_URL },
  { key: "investmentSignUrl", label: LABEL_INVESTMENT_SIGN_URL },
  { key: "investmentSignThankYouUrl", label: LABEL_INVESTMENT_SIGN_THANK_YOU_URL },
];

const getLinkTemplatesItems = (organization: Organization): JSX.Element[] => {
  const { domain, linkTemplates = {} } = organization;

  return LINK_TEMPLATE_FIELDS
    .map(({ key, label }) => {
      const customUrl = linkTemplates[key];

      const hasNoUrl =
        customUrl === null ||
        customUrl === undefined ||
        customUrl === "";

      const url = hasNoUrl
        ? getDefaultLinkTemplatePath(key, domain)
        : customUrl;

      return {
        label,
        value: renderLinkTemplateValue(url),
      };
    })
    .map(({ label, value }, key) =>
      <Item
        key={key}
        label={renderLabel(label)}
      >
        {value}
      </Item>,
    );
};

export default getLinkTemplatesItems;
